import React from "react"
import { twMerge } from "tailwind-merge"

import placeholderImage from "../../../../assets/images/profile-photo-placeholder.jpg"
import { groupEventCardDateLabel } from "../../../@core/bookableEventType/bookableEventOccurrence.utils"
import { formatBookableEventDurationLabel } from "../../../@core/bookableEventType/bookableEventType.utils"
import GroupEventCardMetaLine from "../../../@core/bookableEventType/GroupEventCardMetaLine"
import {
  FILESTACK_CDN_WIDTHS,
  ensureFilestackCdnResized,
  filestackUrlFromPhotoHandles
} from "../../../utils/filestackCdn"
import { getServicePriceRange, getServiceDurationRange, formatDurationWithSessions } from "../../../utils/utils"
import { getSectionColorScheme } from "../utils/colorModeUtils"
import { formatFontFamily, formatHeadingFontFamily } from "../utils/fontUtils"
import { normalizeServiceImageObjectFit } from "../utils/serviceCardImageUtils"
import { applyButtonTheme, getThemeShapeClasses } from "../utils/themeUtils"
import { buildServiceUrl, buildEventTypeUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import { ScrollRevealItem } from "./shared/ScrollReveal"
import { CARD_IMAGE_HOVER_CLASSES, CARD_IMAGE_OVERLAY_CLASSES } from "./shared/scrollRevealVariants"
import ServiceCardImage from "./shared/ServiceCardImage"

const FeaturedServicesSectionMobile = ({ section, practice, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "double_img_r"
  const selectedServiceIds = settings.selectedServiceIds || []
  const imageObjectFit = normalizeServiceImageObjectFit(settings.imageObjectFit)

  // Get color scheme based on color mode
  const colorScheme = getSectionColorScheme(settings, theme)
  const { themeColors } = colorScheme

  // Use color scheme for colors
  const sectionBackgroundColor = colorScheme.sectionBackground
  const headingColor = colorScheme.headingColor
  const bodyColor = colorScheme.bodyColor

  const handleItemClick = (item) => {
    if (item.itemType === "service" || item.itemType === "package") {
      window.location.href = buildServiceUrl(item, practice)
    } else if (item.itemType === "event") {
      // For event instances, use the slug from bookableEventTypeSlug if available
      const eventSlug = item.bookableEventTypeSlug || item.slug
      window.location.href = buildEventTypeUrl(eventSlug, practice)
    }
  }

  // Get all available items
  // Services includes both services and packages (packages have package: true)
  const allServices = (practice?.services || []).filter((service) => service.public === true)
  const allPracticeServices = allServices.filter((service) => !service.package)
  const allPracticePackages = allServices.filter((service) => service.package === true)
  // Events can be either event types (templates) or specific event instances
  const allPracticeEventTypes = practice?.bookableEventTypes || []
  const allPracticeEventInstances = practice?.bookableEvents || []
  const allPracticeEvents = [...allPracticeEventTypes, ...allPracticeEventInstances]

  // Only show items that are explicitly selected (selectedItemIds)
  // Parse the selectedItemIds to handle the new unified format: "service-123", "package-456", "event-slug"
  const selectedItemIds = settings.selectedItemIds || selectedServiceIds || []
  const selectedItems = selectedItemIds
    .map((id) => {
      // Handle both old format (just ID) and new format (type-id)
      if (typeof id === "string" && id.includes("-")) {
        const [type, itemId] = id.split("-")
        if (type === "service") {
          const service = allPracticeServices.find((s) => s.id?.toString() === itemId)
          return service ? { ...service, itemType: "service" } : null
        }
        if (type === "package") {
          const pkg = allPracticePackages.find((p) => p.id?.toString() === itemId)
          return pkg ? { ...pkg, itemType: "package" } : null
        }
        if (type === "event") {
          // Events can have either numeric ID or slug
          const event = allPracticeEvents.find((e) => e.id?.toString() === itemId || e.slug === itemId)
          return event ? { ...event, itemType: "event" } : null
        }
        return null
      }
      // Old format: just the service ID
      const service = allPracticeServices.find((s) => s.id === id)
      return service ? { ...service, itemType: "service" } : null
    })
    .filter(Boolean)

  // Limit items based on layout
  const maxItems = layout === "list" ? 999 : layout === "highlights_2" ? 2 : layout === "highlights_3" ? 3 : 2
  const allItems = selectedItems.slice(0, maxItems)

  // Get item images for double image layouts (first 2 items)
  const getDoubleImageUrls = () => {
    const items = selectedItems.slice(0, 2)
    return items.map((item) => {
      // Get image URL from various possible sources
      // Check both camelCase (packages) and snake_case (events) properties
      const photoHandles = item.photoHandles || item.filestackPhoto || item.filestack_photo
      return (
        ensureFilestackCdnResized(
          item.imageUrl ||
            item.mediumWebpUrl ||
            item.photoUrl ||
            item.photo_url ||
            filestackUrlFromPhotoHandles(photoHandles, { width: FILESTACK_CDN_WIDTHS.card }),
          { width: FILESTACK_CDN_WIDTHS.card }
        ) || placeholderImage
      )
    })
  }

  const imageUrls = getDoubleImageUrls()

  // Render service card for highlights layout (mobile)
  const renderHighlightCard = (item, index) => {
    // Get display info based on item type
    let price = ""
    let duration = ""
    let locationInfo = ""
    let displayName = item.name || item.title
    let displayDescription = item.description || ""

    if (item.itemType === "service") {
      price = getServicePriceRange(item, practice?.defaultCurrency || "USD")
      duration = getServiceDurationRange(item)
      const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
      locationInfo = [duration, location].filter(Boolean).join(" • ")
    } else if (item.itemType === "package") {
      price = getServicePriceRange(item, practice?.defaultCurrency || "USD")
      const timeLength =
        typeof item.timeLength === "number" ? item.timeLength : item.timeLength?.value || item.timeLength
      duration = timeLength ? formatDurationWithSessions(timeLength, item) : ""
      const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
      locationInfo = [duration, location].filter(Boolean).join(" • ")
    } else if (item.itemType === "event") {
      // Events send amount_cents, not price
      price = item.amount_cents ? `$${(item.amount_cents / 100).toFixed(2)}` : item.price ? `$${item.price}` : ""
      duration = formatBookableEventDurationLabel(item)
      const virtual = item.kind === "virtual" || item.virtual ? "Virtual" : "In-person"
      const capacity = item.capacity ? `${item.capacity} capacity` : ""
      locationInfo = [virtual, capacity].filter(Boolean).join(" • ")
    }

    // Get image URL from various possible sources
    // Check both camelCase (packages) and snake_case (events) properties
    const photoHandles = item.photoHandles || item.filestackPhoto || item.filestack_photo
    const imageUrl =
      ensureFilestackCdnResized(
        item.imageUrl ||
          item.mediumWebpUrl ||
          item.photoUrl ||
          item.photo_url ||
          filestackUrlFromPhotoHandles(photoHandles, { width: FILESTACK_CDN_WIDTHS.card }),
        { width: FILESTACK_CDN_WIDTHS.card }
      ) || placeholderImage

    if (displayDescription && displayDescription.length > 200) {
      displayDescription = displayDescription.substring(0, 200) + "..."
    }

    // Check if this is a package or group event (for tag display)
    const isPackage = item.itemType === "package" || item.type === "package"
    const isGroupEvent = item.type === "group_event" || item.itemType === "event"

    return (
      <ScrollRevealItem key={index} staggerIndex={index + 1} className="group flex w-full flex-col gap-4">
        <div className={twMerge("relative aspect-[396/250] w-full overflow-hidden", getThemeShapeClasses(theme))}>
          <ServiceCardImage
            src={imageUrl}
            alt={displayName}
            objectFit={imageObjectFit}
            hoverClassName={CARD_IMAGE_HOVER_CLASSES}
            onError={(e) => {
              if (e.target.src !== placeholderImage) e.target.src = placeholderImage
            }}
          />
          <div className={CARD_IMAGE_OVERLAY_CLASSES} style={{ backgroundColor: "rgba(0,0,0,0.06)" }} />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-0">
            <div className="flex items-center gap-1.5">
              {price && (
                <span
                  className="text-lg font-semibold"
                  style={{
                    fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                    fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                    color: headingColor,
                    lineHeight: "24px"
                  }}>
                  {price}
                </span>
              )}
              {(isPackage || isGroupEvent) && (
                <div
                  className="rounded px-3 py-1"
                  style={{
                    backgroundColor: theme?.colors?.bgLight || themeColors.bgLight || "#F4ECE9",
                    borderRadius: theme?.borderRadius ? `${parseInt(theme.borderRadius) / 4}px` : "4px"
                  }}>
                  <span
                    className="text-xs font-semibold uppercase tracking-wide"
                    style={{
                      fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                      fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                      color: headingColor,
                      lineHeight: "16px"
                    }}>
                    {isPackage
                      ? item.numberOfSessions
                        ? `${item.numberOfSessions} Session Package`
                        : "Package"
                      : "Group Event"}
                  </span>
                </div>
              )}
            </div>
            {/* Title */}
            <h3
              className="text-[22px] font-bold leading-[32px]"
              style={{
                fontFamily: formatHeadingFontFamily(theme?.fonts?.heading),
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: headingColor
              }}>
              {displayName}
            </h3>
          </div>

          {/* Duration and Location */}
          {item.itemType === "event" ? (
            <GroupEventCardMetaLine
              duration={duration}
              locationInfo={locationInfo}
              dateLabel={groupEventCardDateLabel(item, practice)}
              className="text-sm"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: bodyColor,
                lineHeight: "20px"
              }}
            />
          ) : (
            locationInfo && (
              <p
                className="text-sm"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                  color: bodyColor,
                  lineHeight: "20px"
                }}>
                {locationInfo}
              </p>
            )
          )}

          {/* Description - Limited to 2 lines */}
          {displayDescription && (
            <div
              className="line-clamp-2 text-base"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: bodyColor,
                lineHeight: "24px",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              }}
              dangerouslySetInnerHTML={{ __html: displayDescription }}
            />
          )}

          {/* Details button */}
          <button
            onClick={() => handleItemClick(item)}
            className="inline-flex w-auto items-center justify-center gap-3 self-start border-2 px-10 py-3 transition-opacity hover:opacity-90"
            style={applyButtonTheme(
              {
                backgroundColor: "#FFFFFF",
                borderColor: theme?.colors?.primary || "#BD654F",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.primary || "#BD654F",
                fontSize: "16px",
                lineHeight: "24px"
              },
              theme
            )}>
            Details
          </button>
        </div>
      </ScrollRevealItem>
    )
  }

  // Render double image layout (mobile - stacked images on top, text below)
  const renderDoubleImageLayout = () => (
    <div className="flex flex-col items-center">
      {/* Images Section - Stacked */}
      <div className="relative">
        {/* First image */}
        {imageUrls[0] && (
          <div
            className="relative"
            style={{
              marginLeft: "160.778px",
              marginTop: "0",
              width: "192.222px",
              height: "213.384px",
              transform: "rotate(180deg) scaleY(-1)"
            }}>
            <div
              className={twMerge("size-full overflow-hidden", getThemeShapeClasses(theme))}
              style={{
                transform: "rotate(180deg) scaleY(-1)"
              }}>
              <img
                src={imageUrls[0]}
                alt=""
                className="size-full object-cover"
                style={{
                  transform: "scale(1.4761) translateX(-38.84%) translateY(-0.77%)",
                  objectPosition: "center"
                }}
                onError={(e) => {
                  if (e.target.src !== placeholderImage) e.target.src = placeholderImage
                }}
              />
            </div>
          </div>
        )}
        {/* Second image - overlapping */}
        {imageUrls[1] && (
          <div
            className="absolute"
            style={{
              left: "0",
              top: "137.24px",
              width: "245.622px",
              height: "235.065px"
            }}>
            <div className={twMerge("size-full overflow-hidden", getThemeShapeClasses(theme))}>
              <img
                src={imageUrls[1]}
                alt=""
                className="size-full object-cover"
                style={{
                  transform: "scale(1.0449) translateY(-0.9%)",
                  objectPosition: "center"
                }}
                onError={(e) => {
                  if (e.target.src !== placeholderImage) e.target.src = placeholderImage
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Text Content Section - Removed headline, body, and CTA button */}
      <div className="flex w-full flex-col px-5 pt-5"></div>
    </div>
  )

  // Render section header (title, subtitle and Browse Services button)
  const renderSectionHeader = () => {
    const title = settings.title || "Work with me"
    const subtitle = settings.subtitle || ""

    return (
      <ScrollRevealItem staggerIndex={0} className="mb-10 flex w-full flex-col gap-6">
        <div className="flex w-full items-center justify-between gap-4">
          {/* Title */}
          <h2
            className="text-left text-4xl font-bold leading-[48px]"
            style={{
              fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: headingColor
            }}>
            {title}
          </h2>

          {/* Browse Services Button */}
          <button
            onClick={() => (window.location.href = buildServiceUrl(null, practice))}
            className="inline-flex shrink-0 items-center justify-center gap-3 self-center px-8 py-3 transition-opacity hover:opacity-90"
            style={applyButtonTheme(
              {
                backgroundColor: theme?.colors?.primary || "#BD654F",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.bgLight || "#F4ECE9",
                fontSize: "14px",
                lineHeight: "20px"
              },
              theme
            )}>
            Browse Services
          </button>
        </div>

        {/* Subtitle */}
        {subtitle && (
          <p
            className="text-left text-lg leading-[28px]"
            style={{
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
              color: bodyColor
            }}>
            {subtitle}
          </p>
        )}
      </ScrollRevealItem>
    )
  }

  // Render list layout (mobile - vertical cards like ServicesSection)
  const renderListLayout = () => (
    <div className="flex w-full flex-col gap-8">
      {/* Section Header with Browse Services button */}
      {renderSectionHeader()}

      {/* Service Cards - Vertical Stack (same as highlights for mobile) */}
      <div className="flex flex-col gap-10">{allItems.map((item, index) => renderHighlightCard(item, index))}</div>
    </div>
  )

  // Render highlights layout (mobile - vertical stack)
  const renderHighlightsLayout = () => (
    <div className="flex w-full flex-col gap-10">
      {/* Section Header with Browse Services button */}
      {renderSectionHeader()}

      {/* Service Cards - Vertical Stack */}
      <div className="flex flex-col gap-10">{allItems.map((item, index) => renderHighlightCard(item, index))}</div>
    </div>
  )

  const isListLayout = layout === "list"
  const isDoubleImageLayout = layout === "double_img_r" || layout === "double_img_l"
  const isHighlightsLayout = layout === "highlights_2" || layout === "highlights_3"

  return (
    <section
      className={twMerge("relative py-8", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{
        backgroundColor: sectionBackgroundColor
      }}>
      <div className="mx-auto max-w-[1160px] px-5">
        {isListLayout
          ? renderListLayout()
          : isDoubleImageLayout
          ? renderDoubleImageLayout()
          : isHighlightsLayout
          ? renderHighlightsLayout()
          : renderDoubleImageLayout()}
      </div>
    </section>
  )
}

export default FeaturedServicesSectionMobile
